import React, { useEffect, useState } from "react";
import { Row, Col } from "react-bootstrap";
import { BsFiletypeXls } from "react-icons/bs";
import { CgUnavailable } from "react-icons/cg";
import { FaChartLine, FaFileInvoice, FaFolderOpen } from "react-icons/fa";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const API_URL = process.env.REACT_APP_API_URL;

function ProjectWiseLedger() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ledgerData, setLedgerData] = useState(null);
  const [formData, setFormData] = useState({
    project_id: "",
    start_date: "",
    end_date: "",
  });

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const getAuthToken = () => localStorage.getItem("token");

  useEffect(() => {
    const fetchProjects = async () => {
      const token = getAuthToken();
      if (!token) return;
      try {
        const projectsResponse = await fetch(`${API_URL}/project-list-block`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        const projectsData = await projectsResponse.json();
        if (projectsData.status) {
          setProjects(projectsData.data || []);
        }
      } catch (err) {
        console.error("Error fetching projects:", err);
        Swal.fire("Error", "Failed to load projects", "error");
      }
    };

    fetchProjects();
  }, []);

  const handleProjectChange = (e) => {
    const projectId = e.target.value;
    setFormData((prev) => ({ ...prev, project_id: projectId }));
    setLedgerData(null);
  };

  const handleDateChange = (dates) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);

    if (start && end) {
      setFormData((prev) => ({
        ...prev,
        start_date: start.toISOString().split("T")[0],
        end_date: end.toISOString().split("T")[0],
      }));
    }
  };

  const fetchLedgerData = async () => {
    if (!formData.project_id) {
      Swal.fire("Warning", "Please select a project", "warning");
      return;
    }
    setLoading(true);
    const token = getAuthToken();

    try {
      const params = new URLSearchParams({
        project_id: formData.project_id,
      });
      if (formData.start_date) params.append("start_date", formData.start_date);
      if (formData.end_date) params.append("end_date", formData.end_date);

      const response = await fetch(
        `${API_URL}/project_wise-ledger-report?${params}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();
      if (data.success === "1") {
        setLedgerData(data.data);
        Swal.fire("Success", "Ledger data loaded successfully", "success");
      } else {
        Swal.fire(
          "Error",
          data.message || "Failed to load ledger data",
          "error"
        );
      }
    } catch (err) {
      console.error("Error fetching ledger data:", err);
      Swal.fire("Error", "Failed to load ledger data", "error");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const handleExportExcel = () => {
    if (!ledgerData) return;

    const headers = [
      "Plot No",
      "Block",
      "Customer",
      "Associate",
      "Total Credit (₹)",
      "Total Debit (₹)",
      "Balance (₹)",
      "Total Value (₹)",
    ];

    const rows = ledgerData.plots_ledger.map((item) => [
      item.plot.plot_shop_villa_no,
      item.plot.block_name,
      item.customer.name,
      item.associate.name,
      item.financials.total_credit,
      item.financials.total_debit,
      item.financials.balance,
      item.financials.total_value,
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `project_wise_ledger_report_${formData.project_id}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // 🎨 Summary Card Colors
  const summaryColors = {
    plots: { bg: "#e8f1fb", color: "#1e5799", icon: "🏢" },
    credit: { bg: "#e7f9eb", color: "#059669", icon: "📈" },
    debit: { bg: "#feeaeb", color: "#dc2626", icon: "📉" },
    balance: { bg: "#f3e8ff", color: "#7c3aed", icon: "💰" },
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f0f6ff 0%, #e6f0fa 100%)",
        padding: "24px",
        fontFamily: "'Poppins', 'DM Sans', sans-serif",
      }}
    >
      <div style={{ maxWidth: "1500px", margin: "0 auto" }}>
        {/* 🎯 HEADER */}
        <div
          style={{
            background: "#ffffff",
            borderRadius: "20px 20px 0 0",
            boxShadow: "0 12px 40px rgba(30, 87, 153, 0.1)",
            border: "1.5px solid #e2e8f0",
            borderBottom: "none",
            overflow: "hidden",
          }}
        >
          {/* Gradient Bar */}
          <div
            style={{
              height: "5px",
              background: "linear-gradient(90deg, #1e5799, #2b6cb0, #1e5799)",
            }}
          />

          <div
            style={{
              padding: "24px 30px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "16px",
              flexWrap: "wrap",
              background: "linear-gradient(135deg, #ffffff, #f8fafc)",
              borderBottom: "1.5px solid #e2e8f0",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "14px",
                  background: "linear-gradient(135deg, #1e5799, #2b6cb0)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#ffffff",
                  fontSize: "22px",
                  boxShadow: "0 8px 20px rgba(30, 87, 153, 0.3)",
                  flexShrink: 0,
                }}
              >
                <FaChartLine />
              </div>
              <div>
                <h2
                  style={{
                    margin: 0,
                    fontSize: "22px",
                    fontWeight: "800",
                    color: "#1e3a5f",
                    letterSpacing: "-0.3px",
                  }}
                >
                  Project Wise Ledger
                </h2>
                <p
                  style={{
                    margin: "2px 0 0",
                    fontSize: "12.5px",
                    color: "#64748b",
                    fontWeight: "500",
                  }}
                >
                  View financial ledger report by project
                </p>
              </div>
            </div>

            <button
              onClick={handleExportExcel}
              disabled={!ledgerData}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                padding: "10px 20px",
                borderRadius: "10px",
                border: "none",
                background: ledgerData
                  ? "linear-gradient(135deg, #059669, #047857)"
                  : "#cbd5e1",
                color: "#ffffff",
                fontSize: "13px",
                fontWeight: "700",
                cursor: ledgerData ? "pointer" : "not-allowed",
                transition: "all 0.25s ease",
                boxShadow: ledgerData
                  ? "0 6px 18px rgba(5, 150, 105, 0.3)"
                  : "none",
                opacity: ledgerData ? 1 : 0.6,
              }}
              onMouseEnter={(e) => {
                if (ledgerData) {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow =
                    "0 10px 24px rgba(5, 150, 105, 0.45)";
                }
              }}
              onMouseLeave={(e) => {
                if (ledgerData) {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 6px 18px rgba(5, 150, 105, 0.3)";
                }
              }}
            >
              <BsFiletypeXls style={{ fontSize: "16px" }} />
              Export CSV
            </button>
          </div>
        </div>

        {/* 🎯 BODY */}
        <div
          style={{
            background: "#ffffff",
            borderRadius: "0 0 20px 20px",
            boxShadow: "0 12px 40px rgba(30, 87, 153, 0.1)",
            border: "1.5px solid #e2e8f0",
            borderTop: "none",
            padding: "24px 30px",
          }}
        >
          {/* Filters */}
          <div
            style={{
              background: "#f8fafc",
              borderRadius: "14px",
              padding: "18px",
              marginBottom: "24px",
              border: "1.5px solid #e2e8f0",
            }}
          >
            <Row className="gy-3">
              <Col md={8}>
                <label
                  style={{
                    display: "block",
                    fontSize: "12.5px",
                    fontWeight: "700",
                    color: "#1e3a5f",
                    marginBottom: "6px",
                  }}
                >
                  Project <span style={{ color: "#dc2626" }}>*</span>
                </label>
                <div style={{ position: "relative" }}>
                  <span
                    style={{
                      position: "absolute",
                      left: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "#1e5799",
                      fontSize: "14px",
                      pointerEvents: "none",
                    }}
                  >
                    <FaFolderOpen />
                  </span>
                  <select
                    value={formData.project_id}
                    onChange={handleProjectChange}
                    style={{
                      width: "100%",
                      padding: "11px 14px 11px 38px",
                      borderRadius: "10px",
                      border: "1.5px solid #e2e8f0",
                      background: "#ffffff",
                      fontSize: "13.5px",
                      fontWeight: "500",
                      color: "#1e3a5f",
                      cursor: "pointer",
                      outline: "none",
                      transition: "all 0.25s ease",
                      boxSizing: "border-box",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#1e5799";
                      e.target.style.boxShadow = "0 0 0 4px rgba(30,87,153,0.1)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#e2e8f0";
                      e.target.style.boxShadow = "none";
                    }}
                  >
                    <option value="">Select Project</option>
                    {projects.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))}
                  </select>
                </div>
              </Col>

              <Col md={4} className="d-flex align-items-end">
                <button
                  onClick={fetchLedgerData}
                  disabled={!formData.project_id || loading}
                  style={{
                    width: "100%",
                    padding: "11px 20px",
                    borderRadius: "10px",
                    border: "none",
                    background: formData.project_id
                      ? "linear-gradient(135deg, #1e5799, #2b6cb0)"
                      : "#cbd5e1",
                    color: "#ffffff",
                    fontSize: "13.5px",
                    fontWeight: "700",
                    cursor: formData.project_id ? "pointer" : "not-allowed",
                    transition: "all 0.25s ease",
                    boxShadow: formData.project_id
                      ? "0 6px 18px rgba(30, 87, 153, 0.3)"
                      : "none",
                    opacity: formData.project_id ? 1 : 0.6,
                  }}
                >
                  {loading ? "Loading..." : "View Ledger"}
                </button>
              </Col>
            </Row>
          </div>

          {/* Loading */}
          {loading && (
            <div
              style={{
                textAlign: "center",
                padding: "60px 20px",
              }}
            >
              <div
                style={{
                  width: "50px",
                  height: "50px",
                  border: "4px solid #e2e8f0",
                  borderTop: "4px solid #1e5799",
                  borderRadius: "50%",
                  margin: "0 auto 16px",
                  animation: "spin 0.8s linear infinite",
                }}
              />
              <p
                style={{
                  color: "#64748b",
                  fontWeight: "600",
                  fontSize: "14px",
                }}
              >
                Loading ledger data...
              </p>
            </div>
          )}

          {/* Ledger Data */}
          {!loading && ledgerData && (
            <>
              {/* Project Info */}
              <div
                style={{
                  background: "linear-gradient(135deg, #1e5799, #2b6cb0)",
                  borderRadius: "14px",
                  padding: "18px 22px",
                  marginBottom: "20px",
                  color: "#ffffff",
                  display: "flex",
                  alignItems: "center",
                  gap: "14px",
                  boxShadow: "0 8px 24px rgba(30, 87, 153, 0.25)",
                }}
              >
                <div
                  style={{
                    width: "46px",
                    height: "46px",
                    borderRadius: "12px",
                    background: "rgba(255,255,255,0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "22px",
                    flexShrink: 0,
                  }}
                >
                  🏗️
                </div>
                <div>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "11.5px",
                      opacity: 0.85,
                      fontWeight: "600",
                      textTransform: "uppercase",
                      letterSpacing: "1px",
                    }}
                  >
                    Project
                  </p>
                  <h4
                    style={{
                      margin: "2px 0 0",
                      fontSize: "18px",
                      fontWeight: "800",
                    }}
                  >
                    {ledgerData.project.name}
                  </h4>
                </div>
              </div>

              {/* Summary Cards */}
              <Row className="gy-3 mb-4">
                {[
                  {
                    label: "Total Plots",
                    value: ledgerData.summary.total_plots,
                    ...summaryColors.plots,
                  },
                  {
                    label: "Total Credit",
                    value: formatCurrency(
                      parseFloat(ledgerData.summary.total_credit)
                    ),
                    ...summaryColors.credit,
                  },
                  {
                    label: "Total Debit",
                    value: formatCurrency(
                      parseFloat(ledgerData.summary.total_debit)
                    ),
                    ...summaryColors.debit,
                  },
                  {
                    label: "Total Balance",
                    value: formatCurrency(
                      parseFloat(ledgerData.summary.total_balance)
                    ),
                    ...summaryColors.balance,
                  },
                ].map((card, i) => (
                  <Col md={3} key={i}>
                    <div
                      style={{
                        background: "#ffffff",
                        borderRadius: "14px",
                        padding: "18px",
                        border: "1.5px solid #e2e8f0",
                        position: "relative",
                        overflow: "hidden",
                        boxShadow: "0 4px 14px rgba(30, 87, 153, 0.06)",
                        transition: "all 0.3s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-4px)";
                        e.currentTarget.style.boxShadow =
                          "0 12px 28px rgba(30, 87, 153, 0.15)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow =
                          "0 4px 14px rgba(30, 87, 153, 0.06)";
                      }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "4px",
                          height: "100%",
                          background: card.color,
                        }}
                      />
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <p
                            style={{
                              fontSize: "11.5px",
                              fontWeight: "700",
                              color: "#64748b",
                              margin: 0,
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                            }}
                          >
                            {card.label}
                          </p>
                          <h3
                            style={{
                              fontSize: "22px",
                              fontWeight: "800",
                              color: card.color,
                              margin: "8px 0 0",
                              letterSpacing: "-0.5px",
                            }}
                          >
                            {card.value}
                          </h3>
                        </div>
                        <div
                          style={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "10px",
                            background: card.bg,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "18px",
                            flexShrink: 0,
                          }}
                        >
                          {card.icon}
                        </div>
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>

              {/* Table */}
              <div
                style={{
                  overflowX: "auto",
                  borderRadius: "14px",
                  border: "1.5px solid #e2e8f0",
                }}
              >
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "13px",
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        background: "linear-gradient(135deg, #1e5799, #2b6cb0)",
                      }}
                    >
                      {[
                        "Sr No.",
                        "Plot No.",
                        "Block",
                        "Customer Name",
                        "Customer Mobile",
                        "Associate Name",
                        "Associate Mobile",
                        "Credit (₹)",
                        "Debit (₹)",
                        "Balance (₹)",
                      ].map((th, i) => (
                        <th
                          key={i}
                          style={{
                            padding: "14px 12px",
                            color: "#ffffff",
                            fontWeight: "700",
                            fontSize: "11.5px",
                            textAlign: "left",
                            letterSpacing: "0.5px",
                            textTransform: "uppercase",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {th}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {/* {ledgerData.plots_ledger.map((item, index) => {
                      const balance = parseFloat(item.financials.balance);
                      return (
                        <tr
                          key={item.plot.id || index}
                          style={{
                            borderBottom: "1px solid #f1f5f9",
                            transition: "background 0.2s ease",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.background = "#f8fafc")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.background = "transparent")
                          }
                        >
                          <td
                            style={{
                              padding: "12px",
                              fontWeight: "700",
                              color: "#1e5799",
                            }}
                          >
                            {index + 1}
                          </td>
                          <td
                            style={{
                              padding: "12px",
                              fontWeight: "600",
                              color: "#1e3a5f",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {item.plot.plot_shop_villa_no}
                          </td>
                          <td
                            style={{
                              padding: "12px",
                              color: "#334155",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {item.plot.block_name}
                          </td>
                          <td
                            style={{
                              padding: "12px",
                              fontWeight: "600",
                              color: "#1e3a5f",
                            }}
                          >
                            {item.customer.name}
                          </td>
                          <td
                            style={{
                              padding: "12px",
                              color: "#64748b",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {item.customer.mobile}
                          </td>
                          <td
                            style={{
                              padding: "12px",
                              fontWeight: "600",
                              color: "#334155",
                            }}
                          >
                            {item.associate.name}
                          </td>
                          <td
                            style={{
                              padding: "12px",
                              color: "#64748b",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {item.associate.mobile}
                          </td>
                          <td
                            style={{
                              padding: "12px",
                              fontWeight: "700",
                              color: "#059669",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {formatCurrency(
                              parseFloat(item.financials.total_credit)
                            )}
                          </td>
                          <td
                            style={{
                              padding: "12px",
                              fontWeight: "700",
                              color: "#dc2626",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {formatCurrency(
                              parseFloat(item.financials.total_debit)
                            )}
                          </td>
                          <td
                            style={{
                              padding: "12px",
                              fontWeight: "700",
                              color: balance >= 0 ? "#1e5799" : "#dc2626",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {formatCurrency(balance)}
                          </td>
                        </tr>
                      );
                    })} */}

                    {ledgerData.plots_ledger.map((item, index) => {
                      const balance = parseFloat(item.financials?.balance || 0);
                      return (
                        <tr
                          key={item.plot?.id || index}
                          style={{
                            borderBottom: "1px solid #f1f5f9",
                            transition: "background 0.2s ease",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.background = "#f8fafc")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.background = "transparent")
                          }
                        >
                          <td
                            style={{
                              padding: "12px",
                              fontWeight: "700",
                              color: "#1e5799",
                            }}
                          >
                            {index + 1}
                          </td>
                          <td
                            style={{
                              padding: "12px",
                              fontWeight: "600",
                              color: "#1e3a5f",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {item.plot?.plot_shop_villa_no || "NA"}
                          </td>
                          <td
                            style={{
                              padding: "12px",
                              color: "#334155",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {item.plot?.block_name || "NA"}
                          </td>
                          <td
                            style={{
                              padding: "12px",
                              fontWeight: "600",
                              color: "#1e3a5f",
                            }}
                          >
                            {item.customer?.name || "NA"}
                          </td>
                          <td
                            style={{
                              padding: "12px",
                              color: "#64748b",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {item.customer?.mobile || "NA"}
                          </td>
                          <td
                            style={{
                              padding: "12px",
                              fontWeight: "600",
                              color: "#334155",
                            }}
                          >
                            {item.associate?.name || "NA"}
                          </td>
                          <td
                            style={{
                              padding: "12px",
                              color: "#64748b",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {item.associate?.mobile || "NA"}
                          </td>
                          <td
                            style={{
                              padding: "12px",
                              fontWeight: "700",
                              color: "#059669",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {formatCurrency(parseFloat(item.financials?.total_credit || 0))}
                          </td>
                          <td
                            style={{
                              padding: "12px",
                              fontWeight: "700",
                              color: "#dc2626",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {formatCurrency(parseFloat(item.financials?.total_debit || 0))}
                          </td>
                          <td
                            style={{
                              padding: "12px",
                              fontWeight: "700",
                              color: balance >= 0 ? "#1e5799" : "#dc2626",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {formatCurrency(balance)}
                          </td>
                        </tr>
                      );
                    })}

                    {ledgerData.plots_ledger.length === 0 && (
                      <tr>
                        <td
                          colSpan="10"
                          style={{
                            padding: "50px",
                            textAlign: "center",
                            color: "#94a3b8",
                            fontWeight: "600",
                          }}
                        >
                          📭 No plots found for the selected project
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Empty State */}
          {!loading && !ledgerData && (
            <div
              style={{
                textAlign: "center",
                padding: "70px 20px",
              }}
            >
              <div
                style={{
                  width: "80px",
                  height: "80px",
                  borderRadius: "20px",
                  background: "#e8f1fb",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 20px",
                  fontSize: "36px",
                  color: "#1e5799",
                }}
              >
                <FaFileInvoice />
              </div>
              <h4
                style={{
                  color: "#1e3a5f",
                  fontWeight: "800",
                  marginBottom: "8px",
                }}
              >
                No Data Available
              </h4>
              <p
                style={{
                  color: "#64748b",
                  fontSize: "13.5px",
                  margin: 0,
                }}
              >
                Please select a project to view the ledger report
              </p>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .react-datepicker-wrapper {
          width: 100%;
        }
        .react-datepicker__input-container {
          width: 100%;
        }
      `}</style>
    </div>
  );
}

export default ProjectWiseLedger;